import React from 'react';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { TwoLineCell } from '../../../components/two-line-cell';
import { usePagedList } from '../../../hooks/use-paged-list';
import { formatDate, formatDateTime } from '../../../utils/date';
import { formatCurrency } from '../../../utils/format';
import { useBiddingSearchController } from './context/bidding-search-controller';
import { useBiddingSearchState } from './context/bidding-search-context';
import BiddingAppService from '../../../application/platform/apm/bidding/bidding-app-service';
import { STATUS_LICITACAO_LABEL, StatusLicitacao, enumOptions } from '../../../model/apm/apm-enums';
import { FilterInput, FilterSelect } from '../../../components/filter-fields';
import { lookupOptions } from '../../../utils/filter-options';
import { PagedGrid } from '../../../components/paged-grid';
import { AttachmentsAction, DeleteAction, EditAction } from '../../../components/row-actions';
import { BIDDING_STATUS_COLOR } from './components/bidding-status';
import { BiddingDrawer } from './components/bidding-drawer';

export function BiddingSearchComponent() {
    const ctrl = useBiddingSearchController();
    const { parameters, searchTrigger, modalities, managementUnits, documentTypes, drawer } = useBiddingSearchState();
    const list = usePagedList(BiddingAppService.list, parameters, searchTrigger, 'Não foi possível carregar as licitações');

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        {
            field: 'number', headerName: 'Licitação', width: 220,
            renderCell: (params) => <TwoLineCell primary={params.value} secondary={params.row.modality} bold />
        },
        { field: 'managementUnit', headerName: 'Unidade gestora', flex: 1, minWidth: 200 },
        {
            field: 'openingDate', headerName: 'Abertura', width: 170,
            renderCell: (params) => (
                <TwoLineCell
                    primary={formatDateTime(params.value)}
                    secondary={params.row.publishDate ? `Publicada em ${formatDate(params.row.publishDate)}` : undefined}
                />
            )
        },
        {
            field: 'status', headerName: 'Situação', width: 170,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                    <Chip size="small" color={BIDDING_STATUS_COLOR[params.value as StatusLicitacao] || 'default'} label={STATUS_LICITACAO_LABEL[params.value as StatusLicitacao] || '-'} />
                    {params.row.homologatedAmount != null && (
                        <Typography variant="caption" color="text.secondary">{formatCurrency(params.row.homologatedAmount)}</Typography>
                    )}
                </Box>
            )
        },
        {
            field: 'actions', headerName: 'Ações', width: 130, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <AttachmentsAction fileCount={params.row.fileCount} onClick={() => ctrl.openDrawer(params.row)} />
                    <EditAction onClick={() => ctrl.openDrawer(params.row)} />
                    <DeleteAction onClick={() => ctrl.handleDelete(params.row)} />
                </React.Fragment>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Licitações"
                    subtitle="Processos licitatórios e seus documentos publicados no portal"
                    breadcrumbs={[{ label: 'Licitações' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                    <Grid item xs={12} md={5}>
                        <FilterInput label="Número ou objeto" name="input-term" value={parameters.term} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <FilterSelect label="Modalidade" name="select-modality" value={parameters.modalityId} options={lookupOptions(modalities)} emptyLabel="Todas" onChange={ctrl.handleOnChange} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FilterSelect label="Situação" name="select-status" value={parameters.status} options={enumOptions(STATUS_LICITACAO_LABEL)} emptyLabel="Todas" onChange={ctrl.handleOnChange} />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FilterInput label="Ano de publicação" name="input-year" type="number" value={parameters.year} onChange={ctrl.handleOnChange} onSearch={ctrl.handleSearch} />
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <PagedGrid
                    columns={columns}
                    list={list}
                    parameters={parameters}
                    onPaginationChange={ctrl.handlePaginationChange}
                    addLabel="Nova Licitação"
                    onAdd={() => ctrl.openDrawer()}
                    rowHeight={60}
                />
            </Grid>

            <BiddingDrawer
                open={drawer.open}
                initial={drawer.bidding}
                files={drawer.files}
                modalities={modalities}
                managementUnits={managementUnits}
                documentTypes={documentTypes}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.handleSave}
                onUploadFile={ctrl.handleUploadFile}
                onDeleteFile={ctrl.handleDeleteFile}
            />
        </React.Fragment>
    );
}
